import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { AddPhotoAlternate, Close, Image as ImageIcon } from '@mui/icons-material';
import { useMutation } from '@apollo/client';
import {
  DELETE_STAFF_PORTFOLIO_IMAGE,
  UPLOAD_STAFF_PORTFOLIO_IMAGES,
} from '../../graphql/mutations';

interface PortfolioItem {
  id: string;
  url: string;
}

interface PortfolioUploaderProps {
  staffId: string;
  portfolio: PortfolioItem[];
  onChange?: () => void; // Parent refetches after mutation.
}

// Limits kept symmetric with the customer-side expectations: any image, up to
// 5MB each, max 12 at a time so one drop doesn't hammer the server.
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_PER_BATCH = 12;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // readAsDataURL yields "data:<mime>;base64,<payload>" — strip the prefix.
      const result = reader.result as string;
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function PortfolioUploader({ staffId, portfolio, onChange }: PortfolioUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [uploadImages] = useMutation(UPLOAD_STAFF_PORTFOLIO_IMAGES);
  const [deleteImage] = useMutation(DELETE_STAFF_PORTFOLIO_IMAGE);

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setError(null);

    if (files.length > MAX_PER_BATCH) {
      setError(`You can upload at most ${MAX_PER_BATCH} images at a time.`);
      return;
    }
    for (const f of files) {
      if (!f.type.startsWith('image/')) {
        setError(`${f.name} is not an image.`);
        return;
      }
      if (f.size > MAX_FILE_SIZE) {
        setError(`${f.name} is larger than 5MB.`);
        return;
      }
    }

    setUploading(true);
    try {
      const encoded = await Promise.all(
        files.map(async (f) => ({
          fileName: f.name,
          fileData: await fileToBase64(f),
        }))
      );
      const res = await uploadImages({
        variables: { staffId, images: encoded },
      });
      const result = res.data?.uploadStaffPortfolioImages;
      if (result?.ok) {
        onChange?.();
      } else {
        setError(result?.errors?.[0] ?? 'Upload failed. Please try again.');
      }
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (imageId: string) => {
    setDeletingId(imageId);
    setError(null);
    try {
      const res = await deleteImage({ variables: { imageId } });
      const result = res.data?.deleteStaffPortfolioImage;
      if (result?.ok) {
        onChange?.();
      } else {
        setError(result?.errors?.[0] ?? 'Delete failed. Please try again.');
      }
    } catch {
      setError('Delete failed. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      {/* Upload CTA */}
      <Box
        sx={{
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'border-color 0.15s',
          '&:hover': { borderColor: 'primary.main' },
          mb: 2,
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => handleFiles(e.target.files)}
        />
        {uploading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Uploading…
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <AddPhotoAlternate sx={{ fontSize: 40, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>
              Click to upload portfolio images
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Up to {MAX_PER_BATCH} images at a time, 5MB each
            </Typography>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Existing images */}
      {portfolio.length === 0 ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            color: 'text.secondary',
            bgcolor: 'action.hover',
            borderRadius: 2,
          }}
        >
          <ImageIcon sx={{ fontSize: 32, opacity: 0.4, mb: 1 }} />
          <Typography variant="caption" display="block">
            No portfolio images yet.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 1.5,
          }}
        >
          {portfolio.map((item) => (
            <Box
              key={item.id}
              sx={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: 2,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
                '&:hover .delete-btn': { opacity: 1 },
              }}
            >
              <img
                src={item.url}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <IconButton
                className="delete-btn"
                size="small"
                onClick={() => handleDelete(item.id)}
                disabled={deletingId === item.id}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  opacity: { xs: 1, md: 0 },
                  transition: 'opacity 0.15s',
                  '&:hover': { bgcolor: 'error.main' },
                }}
              >
                {deletingId === item.id ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <Close fontSize="small" />
                )}
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
